<template>
	<div class="animated fadeIn">

		<b-row class="mb-3 align-items-center">
			<b-col>
				<h4 class="mb-0"><i class="fa fa-server mr-2 text-primary"></i>Systems</h4>
				<small class="text-muted">Tipsoi server instances, and what the growth dataset covers</small>
			</b-col>
			<b-col class="text-right">
				<b-button variant="outline-secondary" size="sm" @click="load" :disabled="loading">
					<i class="icon-reload mr-1"></i>Refresh
				</b-button>
			</b-col>
		</b-row>

		<div v-if="loading" class="text-center py-5">
			<b-spinner variant="info" style="width:3rem;height:3rem;"></b-spinner>
		</div>

		<div v-else>
			<!-- GAD source status -->
			<b-card no-body class="mb-3">
				<div slot="header">
					<i class="icon-database mr-2"></i>Growth data source
				</div>
				<b-card-body class="py-3">
					<b-row>
						<b-col md="3" class="mb-2">
							<div class="meta-label">Status</div>
							<div>
								<span class="state" :class="gadAvailable ? 'state-UP' : 'state-DOWN'">
									<span class="dot"></span>{{ gadAvailable ? 'Connected' : 'Unavailable' }}
								</span>
							</div>
						</b-col>
						<b-col md="3" class="mb-2">
							<div class="meta-label">Database</div>
							<div><code>tipsoi_analitics</code></div>
						</b-col>
						<b-col md="3" class="mb-2">
							<div class="meta-label">Latest snapshot</div>
							<div>{{ latestDate || '—' }}</div>
						</b-col>
						<b-col md="3" class="mb-2">
							<div class="meta-label">Clients covered</div>
							<div class="num">{{ formatNumber(summary.totalClients) }}</div>
						</b-col>
					</b-row>
					<b-alert v-if="!gadAvailable" show variant="warning" class="mb-0 mt-2 small">
						{{ gadError || 'The GAD database could not be reached on this environment.' }}
					</b-alert>
				</b-card-body>
			</b-card>

			<!-- What the dataset is actually segmented by -->
			<b-alert show variant="light" class="border small text-muted mb-3">
				<i class="fa fa-info-circle mr-1 text-primary"></i>
				Growth data in <code>growth_analitics</code> is <strong>not segmented per server
				instance</strong> — it is recorded per client and grouped by date, reseller, sales
				channel and package tier. Per-system client and device counts are therefore not
				available. The breakdowns below are the dimensions the dataset does carry; the
				system inventory underneath is the configured instance list.
			</b-alert>

			<b-row class="mb-3">
				<b-col sm="6" md="3" class="mb-2">
					<div class="stat-card">
						<div class="stat-num">{{ systems.length }}</div>
						<div class="stat-label"><i class="fa fa-server mr-1"></i>Systems configured</div>
					</div>
				</b-col>
				<b-col sm="6" md="3" class="mb-2">
					<div class="stat-card">
						<div class="stat-num">{{ counts.resellers }}</div>
						<div class="stat-label">Resellers in data</div>
					</div>
				</b-col>
				<b-col sm="6" md="3" class="mb-2">
					<div class="stat-card">
						<div class="stat-num">{{ counts.channels }}</div>
						<div class="stat-label">Sales channels</div>
					</div>
				</b-col>
				<b-col sm="6" md="3" class="mb-2">
					<div class="stat-card">
						<div class="stat-num">{{ counts.tiers }}</div>
						<div class="stat-label">Package tiers</div>
					</div>
				</b-col>
			</b-row>

			<!-- System inventory -->
			<b-row class="mb-2 align-items-center">
				<b-col md="6" class="mb-2 mb-md-0">
					<b-input-group size="sm">
						<b-input-group-prepend is-text><i class="fa fa-search"></i></b-input-group-prepend>
						<b-form-input v-model="search" type="search" placeholder="Search system name or code…"/>
					</b-input-group>
				</b-col>
				<b-col md="6" class="text-md-right">
					<b-form-radio-group v-model="categoryFilter" size="sm" buttons button-variant="outline-secondary">
						<b-form-radio value="ALL">All</b-form-radio>
						<b-form-radio value="HRM">HRM</b-form-radio>
						<b-form-radio value="CS">CS</b-form-radio>
					</b-form-radio-group>
				</b-col>
			</b-row>

			<b-card no-body>
				<div class="table-responsive">
					<table class="table table-sm table-striped table-hover mb-0">
						<thead>
							<tr>
								<th>System</th>
								<th>Code</th>
								<th>Category</th>
								<th>Project</th>
								<th>API</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="s in filtered" :key="s.code">
								<td>
									{{ s.name }}
									<b-badge v-if="s.local" variant="light" class="ml-1">local</b-badge>
								</td>
								<td><code>{{ s.code }}</code></td>
								<td><b-badge :variant="categoryVariant(s.category)">{{ s.category }}</b-badge></td>
								<td class="num">{{ s.projectId || '—' }}</td>
								<td>
									<b-badge :variant="s.apiConfigured ? 'success' : 'light'">
										{{ s.apiConfigured ? 'wired' : 'not set' }}
									</b-badge>
								</td>
							</tr>
							<tr v-if="!filtered.length">
								<td colspan="5" class="text-center text-muted py-4">No systems match this filter.</td>
							</tr>
						</tbody>
					</table>
				</div>
			</b-card>

			<b-alert v-if="loadError" show variant="warning" class="mt-3 small">{{ loadError }}</b-alert>
		</div>
	</div>
</template>

<script>
import issueConfigService from '@/services/issueConfigService';
import analyticsService from '@/services/analyticsService';

export default {
	name: 'AnalyticsSystems',

	data() {
		return {
			loading: false,
			loadError: '',
			search: '',
			categoryFilter: 'ALL',
			systems: [],
			summary: {},
			growthByDay: [],
			gadAvailable: false,
			gadError: '',
			counts: {resellers: 0, channels: 0, tiers: 0}
		};
	},

	computed: {
		filtered() {
			const term = this.search.trim().toLowerCase();
			return this.systems.filter(s => {
				if (this.categoryFilter !== 'ALL' && s.category !== this.categoryFilter) return false;
				if (!term) return true;
				return (s.name || '').toLowerCase().indexOf(term) !== -1
					|| (s.code || '').toLowerCase().indexOf(term) !== -1;
			});
		},
		latestDate() {
			if (!this.growthByDay.length) return null;
			const row = this.growthByDay[0];
			return row.date || row.day || null;
		}
	},

	methods: {
		load() {
			this.loading = true;
			this.loadError = '';

			issueConfigService.getServers((servers) => {
				this.systems = (servers || []).map(s => ({
					code: s.code,
					name: s.name || s.code,
					category: s.category || 'OTHER',
					projectId: s.projectId,
					local: !!s.local,
					apiConfigured: !!s.apiConfigured
				}));

				analyticsService.getSummary(30, (data) => {
					this.loading = false;
					if (!data || data.available === false) {
						this.gadAvailable = false;
						this.gadError = (data && data.error) || '';
						return;
					}
					this.gadAvailable = true;
					this.summary = data;
					this.growthByDay = data.growthByDay || [];
					this.counts = {
						resellers: (data.topResellers || []).length,
						channels: (data.channelBreakdown || []).length,
						tiers: (data.packageTiers || []).length
					};
				}, (err) => {
					// The inventory still renders — only the growth panel degrades.
					this.loading = false;
					this.gadAvailable = false;
					this.gadError = err || '';
				});
			}, (err) => {
				this.loading = false;
				this.loadError = err || 'Failed to load systems';
			});
		},

		formatNumber(value) {
			if (value === null || value === undefined || value === '') return '—';
			const n = Number(value);
			return isNaN(n) ? value : n.toLocaleString();
		},

		categoryVariant(category) {
			if (category === 'HRM') return 'primary';
			if (category === 'CS') return 'info';
			if (category === 'ADMIN') return 'secondary';
			return 'light';
		}
	},

	created() {
		this.load();
	}
};
</script>

<style scoped>
.stat-card {
	background: #fff;
	border: 1px solid #e4e7ea;
	border-radius: 4px;
	padding: 0.75rem 1rem;
}

.stat-num {
	font-size: 1.5rem;
	font-weight: 600;
	line-height: 1.2;
	font-variant-numeric: tabular-nums;
}

.stat-label {
	font-size: 0.75rem;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #73818f;
}

.meta-label {
	font-size: 0.72rem;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #73818f;
}

.state {
	display: inline-flex;
	align-items: center;
	font-size: 0.85rem;
}

.state .dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	margin-right: 0.4rem;
	background: #c8ced3;
}

.state-UP { color: #2f8f52; }
.state-UP .dot { background: #4dbd74; }
.state-DOWN { color: #c8433f; }
.state-DOWN .dot { background: #f86c6b; }

.num {
	font-variant-numeric: tabular-nums;
}
</style>
